import { useEffect, useMemo, useRef, useState } from "react";
import { scrollBoxToBottom } from "@/lib/utils";
import { mutate as swrMutate } from "swr";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DashLayout from "@/components/DashLayout";
import BodyMap from "@/components/BodyMap";
import DailyCheckinFields from "@/components/DailyCheckinFields";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Sparkles, ClipboardCheck, TrendingUp, AlertTriangle, ShieldCheck, MessageCircle, Lock, ArrowRight, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, CheckCircle2, CalendarClock, Shield, Eye, EyeOff, Maximize2, BookOpen, Flag } from "lucide-react";

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token") || ""}` });

/* -------------------- Recovery score gauge -------------------- */
function ScoreGauge({ score, risk, trend }) {
  const s = score ?? 0;
  const stroke = s >= 70 ? "#22C55E" : s >= 45 ? "#F59E0B" : "#EF4444";
  const circ = 2 * Math.PI * 62;
  const dash = (s / 100) * circ;
  return (
    <div className="fl-card p-6" data-testid="recovery-score-card">
      <div className="fl-label text-[color:var(--fl-orange)] mb-2">Recovery Score</div>
      <div className="flex items-center gap-6">
        <div className="relative w-36 h-36">
          <svg viewBox="0 0 140 140" className="w-full h-full -rotate-90">
            <circle cx="70" cy="70" r="62" stroke="var(--fl-border)" strokeWidth="10" fill="none" />
            <circle cx="70" cy="70" r="62" stroke={stroke} strokeWidth="10" fill="none"
                    strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                    style={{ transition: "stroke-dasharray 700ms cubic-bezier(0.22,1,0.36,1)" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="fl-heading text-4xl" style={{ color: "var(--fl-text)" }}>{score ?? "—"}</div>
            <div className="text-xs uppercase tracking-widest" style={{ color: stroke }}>{risk ?? ""}</div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm mb-2" style={{ color: "var(--fl-muted)" }}>Last 14 days</div>
          <div className="h-16 flex items-end gap-1">
            {(trend || []).slice(-14).map((p, i) => (
              <div key={p.date + i} className="flex-1 rounded-t"
                   style={{ height: `${p.score}%`, background: p.score >= 70 ? "#22C55E" : p.score >= 45 ? "#F59E0B" : "#EF4444", minWidth: 4, opacity: 0.7 }}
                   title={`${p.date}: ${p.score}`} />
            ))}
            {(!trend || trend.length === 0) && <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>Complete your first check-in to see the trend.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------- Daily check-in -------------------- */
const initialCheckin = {
  pain: 3, sleep_hours: 7, sleep_quality: 3, energy: 3, stress: 3, confidence: 4,
  session_rpe: null, comments: "",
  // iter101.7 · Interactive pain-map markers. Kept as an array of
  // `{x, y, marker_number, pain_before, pain_after, rpe, description}`
  // — same shape /app uses so the two check-ins mirror perfectly.
  pain_locations: [],
};

function Slider({ label, value, min, max, step = 1, onChange, testid, hint }) {
  return (
    <div>
      <div className="flex justify-between items-center">
        <label className="text-xs" style={{ color: "var(--fl-muted)" }}>{label}</label>
        <span className="text-xs font-semibold" style={{ color: "var(--fl-text)" }}>{value}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value ?? ""} onChange={(e) => onChange(step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value, 10))}
             className="w-full accent-[color:var(--fl-orange)]" data-testid={testid} />
      {hint && <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>{hint}</div>}
    </div>
  );
}

function CheckinCard({ onSubmitted }) {
  const [c, setC] = useState(initialCheckin);
  const [busy, setBusy] = useState(false);
  const [adh, setAdh] = useState({ adherence: null, has_schedule: null });
  const [today, setToday] = useState(null);   // {submitted:bool, coach:{...}, daily:{...}}
  const [bodyMapOpen, setBodyMapOpen] = useState(false);
  // iter105 · persist the hide/show state per patient so the collapsed
  // check-in stays collapsed on tab switches. Applies to the ENTIRE daily
  // check-in card (sliders + pain map + submit).
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("fl_checkin_hidden") === "1"; }
    catch { return false; }
  });
  const toggleHidden = () => setHidden((v) => {
    const n = !v;
    try { localStorage.setItem("fl_checkin_hidden", n ? "1" : "0"); } catch { /* noop */ }
    return n;
  });
  const update = (k, v) => setC((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/checkins/today`, { headers: HEADERS() })
      .then((r) => {
        setToday(r.data);
        // Prefill from today's coach row if it exists so patient can update.
        // Body-map markers can live on EITHER row (daily lives on /app,
        // coach lives on /app/coach) — prefer coach when both exist since
        // that's the row this page owns; otherwise fall back to daily.
        const c = r.data?.coach;
        const d = r.data?.daily;
        const pins = (c?.pain_locations && c.pain_locations.length)
          ? c.pain_locations
          : (d?.pain_locations || []);
        if (c) {
          setC((s) => ({
            ...s,
            pain: c.pain ?? s.pain,
            sleep_hours: c.sleep_hours ?? s.sleep_hours,
            sleep_quality: c.sleep_quality ?? s.sleep_quality,
            energy: c.energy ?? s.energy,
            stress: c.stress ?? s.stress,
            confidence: c.confidence ?? s.confidence,
            session_rpe: c.session_rpe ?? s.session_rpe,
            pain_locations: pins,
          }));
        } else if (pins.length) {
          setC((s) => ({ ...s, pain_locations: pins }));
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get(`${API}/stats/adherence`, { headers: HEADERS() })
      .then((r) => setAdh(r.data || { adherence: null, has_schedule: null }))
      .catch(() => {});
  }, []);

  // The check-in values the patient set with the sliders, as one line of
  // plain text. The coach chat shows the submitted `comments` verbatim as the
  // patient's turn, so prefixing this summary is what puts the parameters
  // into the chat instead of leaving them locked in the left-hand panel.
  const checkinSummary = (v) => {
    const parts = [
      `pain ${v.pain}/10`,
      `sleep ${v.sleep_hours}h (quality ${v.sleep_quality}/5)`,
      `energy ${v.energy}/5`,
      `stress ${v.stress}/5`,
      `confidence ${v.confidence}/5`,
      v.session_rpe === null || v.session_rpe === "" ? null : `exercise difficulty ${v.session_rpe}`,
      v.pain_locations?.length ? `${v.pain_locations.length} pain marker${v.pain_locations.length === 1 ? "" : "s"} on the body map` : null,
    ].filter(Boolean);
    return `Check-in — ${parts.join(" · ")}.`;
  };
  // The chat bubble is exactly the values. There is no free-text box on this
  // page any more, so nothing else belongs in `comments` — and composing it
  // fresh every time keeps it idempotent across the server round-trip.
  const submit = async () => {
    setBusy(true);
    try {
      // iter111.4 · Prefer the patient's typed comment (surfaced to the AI
      // Coach as a `daily_checkin` bubble). Fall back to the auto-generated
      // parameter summary when the box is empty so the coach still gets
      // context.
      const typed = (c.comments || "").trim();
      const summary = checkinSummary(c);
      const comments = typed || summary;
      const payload = { ...c, comments, session_rpe: c.session_rpe === null ? null : Number(c.session_rpe) };
      const res = await axios.post(`${API}/coach/checkin`, payload, { headers: HEADERS() });
      toast.success(`Recovery Score: ${res.data.score}`);
      swrMutate("wellness:merged");
      setToday({ submitted: true, coach: res.data, daily: null,
                  next_allowed_at: res.data?.next_allowed_at
                    || new Date(Date.now() + 6 * 3600_000).toISOString() });
      // Batch 10A · The server fires the AI Coach reaction as a background
      // task inside POST /coach/checkin — it runs server-side and completes
      // independently of this UI (navigating away, hiding the card, refreshing
      // or closing the app cannot interrupt it). The client no longer posts a
      // fallback /coach/chat message: that used a DIFFERENT event_id and could
      // produce a duplicate coach reply + duplicate clinician proposal for a
      // single check-in. CoachChat polls /coach/history to surface the reply.
      // The submitted values are what the coach chat renders as the patient's
      // message, so scroll the chat into view to show them arriving.
      setTimeout(() => {
        document.getElementById("coach-chat-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      onSubmitted?.(res.data);
    } catch (err) {
      // iter105 · When the backend rejects with 409 the response detail is
      // `{ message, next_allowed_at, hours_remaining }`. Surface that in the
      // toast and refresh the lock state so the UI redraws the countdown.
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 409 && d?.next_allowed_at) {
        setToday((prev) => ({ ...(prev || {}), submitted: true, next_allowed_at: d.next_allowed_at }));
        toast.error(d.message || "You've already checked in recently.");
      } else {
        toast.error(d || err?.response?.data?.detail || "Couldn't submit check-in.");
      }
    } finally { setBusy(false); }
  };

  const alreadyToday = today?.submitted;
  // iter101 · Read the 6-hour lock timestamp from the shared /checkins/today
  // endpoint. Patients may still edit today's row — we only surface the
  // countdown so they know when the next AI coach reaction will fire.
  const nextAllowedAt = today?.next_allowed_at ? new Date(today.next_allowed_at) : null;
  const withinLock = nextAllowedAt && nextAllowedAt.getTime() > Date.now();
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    if (!withinLock) return undefined;
    const t = setInterval(() => setNowTs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [withinLock]);
  const lockCountdown = withinLock
    ? (() => {
        const ms = nextAllowedAt.getTime() - nowTs;
        const h = Math.floor(ms / 3_600_000);
        const m = Math.floor((ms % 3_600_000) / 60_000);
        return `${h}h ${String(m).padStart(2, "0")}m`;
      })()
    : null;
  return (
    <div className="fl-card p-6" data-testid="checkin-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="fl-label text-[color:var(--fl-orange)] flex items-center gap-2"><ClipboardCheck size={14} /> Daily check-in</div>
        <button
          type="button"
          onClick={toggleHidden}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest transition"
          style={{
            color: "#f47a20",
            background: "transparent",
            border: "1px solid rgba(244, 122, 32, 0.28)",
            boxShadow: "0 2px 12px -6px rgba(0, 0, 0, 0.45)",
          }}
          data-testid="checkin-hide-toggle"
          aria-pressed={hidden}
        >
          {hidden ? <><Eye size={12} /> Show</> : <><EyeOff size={12} /> Hide</>}
        </button>
      </div>
      {hidden ? (
        <p className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
          Daily check-in hidden. Click <b>Show</b> to log today's numbers.
        </p>
      ) : (
      <>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="fl-heading text-xl">How are you today?</h3>
        {alreadyToday && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: "color-mix(in oklab, #22C55E 15%, transparent)", color: "#22C55E", border: "1px solid color-mix(in oklab, #22C55E 40%, transparent)" }}
                data-testid="cin-today-badge">
            ✓ Checked in today
          </span>
        )}
        {withinLock && (
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ background: "color-mix(in oklab, var(--fl-orange) 12%, transparent)", color: "var(--fl-orange)", border: "1px solid color-mix(in oklab, var(--fl-orange) 40%, transparent)" }}
                data-testid="cin-lock-badge">
            Coach reaction unlocks in {lockCountdown}
          </span>
        )}
      </div>
      <p className="text-[11px] mt-1 mb-4" style={{ color: "var(--fl-muted-2)" }}>
        {alreadyToday
          ? "Update today's values below — the score refreshes and stays in sync with your dashboard."
          : "Answer once a day. Your responses sync with the home dashboard and drive your Recovery Score."}
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {/* iter111.4 · Slider + comments + body-map layout is owned by the
            shared DailyCheckinFields component so this form and Today
            render byte-identical labels/ranges/hints/order. */}
      </div>
      <DailyCheckinFields value={c} onChange={setC} onExpandBodyMap={() => setBodyMapOpen(true)} adherence={adh.adherence} hasSchedule={adh.has_schedule} disabled={withinLock} />
      {/* Larger dialog view — reuses the SAME BodyMap component + same
          `c.pain_locations` state, so edits mirror instantly and no
          separate submission is issued. */}
      <Dialog open={bodyMapOpen} onOpenChange={setBodyMapOpen}>
        <DialogContent
          data-testid="body-map-dialog"
          className="max-w-[92vw] md:max-w-4xl max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>Mark where it hurts</DialogTitle>
          </DialogHeader>
          <div className="mt-2">
            <BodyMap
              value={c.pain_locations}
              onChange={(next) => update("pain_locations", next)}
              testid="cin-body-map-dialog"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setBodyMapOpen(false)}
              className="fl-btn text-sm"
              data-testid="body-map-dialog-done"
            >
              Done
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* No free-text box here: the check-in is the parameters, and they are
          what gets posted into the AI Coach chat on submit. Notes belong in
          the chat itself, on the right. */}
      <div className="mt-4 flex justify-end">
        {withinLock ? (
          <div
            className="w-full text-center rounded-md px-4 py-3 text-sm font-semibold"
            style={{
              color: "var(--fl-orange)",
              background: "color-mix(in oklab, var(--fl-orange) 8%, transparent)",
              border: "1px solid color-mix(in oklab, var(--fl-orange) 40%, transparent)",
            }}
            data-testid="cin-submit-locked"
            aria-live="polite"
          >
            ✓ Checked in · next check-in unlocks in <strong>{lockCountdown}</strong>
          </div>
        ) : (
          <button
            onClick={submit}
            disabled={busy}
            className="fl-btn text-sm"
            data-testid="cin-submit"
          >
            <Send size={14} /> {busy ? "Saving…" : alreadyToday ? "Update today's check-in" : "Submit check-in"}
          </button>
        )}
      </div>
      </>
      )}
    </div>
  );
}

/* -------------------- Chat -------------------- */
// Source labels rendered on every timeline bubble so the patient can
// tell at a glance where the message came from (their own message vs
// the AI vs an auto-generated check-in echo vs a clinician-approved
// reply). Kept in sync with the backend `source` values written by
// `_persist_chat_message`.
const SOURCE_LABELS = {
  patient_chat: "You",
  daily_checkin: "You · Daily check-in",
  workout_feedback: "You · Session feedback",
  clinician_reply: "Clinician",
  system_error: "AI Coach",
};

const STARTER_CHIPS = [
  "Explain today's programme",
  "I have pain during an exercise",
  "How should this exercise feel?",
  "Recovery advice",
];

function messageTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function relativeTime(iso) {
  // Retained for potential future use; primary label now uses messageTime.
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

// Fresh-conversation policy: the coach view intentionally does NOT clear
// prior transcripts on expand — a mount-time load pulls the last 100 rows
// from /coach/history so refresh preserves context. The chat auto-collapses
// only if the tab crosses midnight so the next interaction is a new day.
function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const SOURCE_TYPE_LABELS = {
  guideline: "Guideline",
  consensus_statement: "Consensus statement",
  systematic_review: "Systematic review",
  meta_analysis: "Meta-analysis",
  research_paper: "Research paper",
  textbook: "Textbook",
  course_material: "Course material",
  functional_lab_protocol: "Functional LAB protocol",
  other: "Source",
};

/* Collapsible, patient-safe Sources list under an evidence-grounded reply.
   Shows title/authors/year/type (+ page/section where available). No scores,
   no internal ids, no file access, no raw excerpts. */
function CoachSources({ sources, id }) {
  if (!sources || sources.length === 0) return null;
  return (
    <details className="mt-1.5 max-w-[80%]" data-testid={`coach-sources-${id}`}>
      <summary className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer select-none"
               style={{ color: "var(--fl-muted)" }}>
        <BookOpen size={12} className="text-[color:var(--fl-orange)]" />
        Sources ({sources.length})
      </summary>
      <div className="mt-1.5 space-y-1.5">
        {sources.map((s, i) => {
          const meta = [
            (s.authors || []).length ? s.authors.join(", ") : null,
            s.year || null,
            SOURCE_TYPE_LABELS[s.source_type] || "Source",
          ].filter(Boolean).join(" · ");
          const loc = [s.page ? `p.${s.page}` : null, s.section || null].filter(Boolean).join(" · ");
          return (
            <div key={i} className="text-[11px] px-2.5 py-2 rounded-md"
                 style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}
                 data-testid={`coach-source-${id}-${i}`}>
              <div className="font-semibold" style={{ color: "var(--fl-text)" }}>{s.title || "Untitled source"}</div>
              {meta && <div style={{ color: "var(--fl-muted)" }}>{meta}</div>}
              {loc && <div style={{ color: "var(--fl-muted-2)" }}>{loc}</div>}
            </div>
          );
        })}
      </div>
    </details>
  );
}

function CoachChat({ userName, refreshTrigger }) {
  const [msgs, setMsgs] = useState([]);
  const [loaded, setLoaded] = useState(false);   // Batch 10A · gate starters until history loads
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [sessionDay, setSessionDay] = useState(todayKey());
  const [awaitingCheckin, setAwaitingCheckin] = useState(false);
  const [feedbackMap, setFeedbackMap] = useState({});          // {message_id: {helpful, note}}
  const [feedbackDraft, setFeedbackDraft] = useState({});      // {message_id: note-in-flight}
  const [feedbackOpen, setFeedbackOpen] = useState({});        // {message_id: bool}
  const [nextReview, setNextReview] = useState(null);          // {scheduled_at, service, clinician_name}
  const [newResponseId, setNewResponseId] = useState(null);    // id of latest unseen AI reply (iter87)
  const [pulse, setPulse] = useState(false);                   // briefly glow the card
  const [flagState, setFlagState] = useState({});              // {message_id: "sending"|"done"}
  const endRef = useRef(null);
  const scrollBoxCoach = useRef(null);

  // Clear the visible chat only when the day rolls over. Do NOT wipe on
  // expand — the mount-time /coach/history load must remain visible.
  useEffect(() => {
    if (!collapsed && todayKey() !== sessionDay) {
      setMsgs([]);
      setInput("");
      setSessionDay(todayKey());
    }
  }, [collapsed, sessionDay]);

  useEffect(() => {
    if (collapsed) return;
    const iv = setInterval(() => {
      if (todayKey() !== sessionDay) setCollapsed(true);
    }, 60_000);
    return () => clearInterval(iv);
  }, [collapsed, sessionDay]);

  useEffect(() => { if (!collapsed) scrollBoxToBottom(scrollBoxCoach.current, true); }, [msgs, collapsed]);

  // When the patient submits a daily check-in on the same page, the backend
  // fires `react_to_checkin_via_coach` as a background task. Poll history
  // every 2s (up to ~20s) so the freshly written coach reply appears in the
  // chat panel without the patient having to refresh.
  useEffect(() => {
    if (!refreshTrigger) return;
    setCollapsed(false);
    setAwaitingCheckin(true);
    setPulse(true);
    // Briefly glow the card so the patient sees the AI Coach light up
    // when the check-in comment fires it. Reset after ~3s.
    const glowTimer = setTimeout(() => setPulse(false), 3000);
    let cancelled = false;
    let attempts = 0;
    const lastSeenId = msgs.length ? msgs[msgs.length - 1].id : null;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/coach/history`, { headers: HEADERS() });
        const list = (r.data || []).filter((m) => m.role !== "system");
        const latest = list[list.length - 1];
        if (latest && latest.role === "assistant" && latest.id !== lastSeenId) {
          setMsgs(list.slice(-20));
          setAwaitingCheckin(false);
          // iter87 · Mark this reply as "new" so the badge stays until
          // the patient interacts with the panel (input focus or scroll).
          setNewResponseId(latest.id);
          return;
        }
      } catch { /* silent */ }
      if (attempts < 10) setTimeout(poll, 2000);
      else setAwaitingCheckin(false);
    };
    poll();
    return () => { cancelled = true; clearTimeout(glowTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Clear the "new response" badge when the patient interacts with the panel.
  const dismissNewBadge = () => setNewResponseId(null);

  // iter79 — load full server history on mount so refresh preserves the
  // conversation. No more "clear on expand" hack.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [h, fb, nr] = await Promise.all([
          axios.get(`${API}/coach/history`, { headers: HEADERS() }),
          axios.get(`${API}/coach/messages/feedback/mine`, { headers: HEADERS() }).catch(() => ({ data: {} })),
          axios.get(`${API}/coach/next-review`, { headers: HEADERS() }).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const list = (h.data || []).filter((m) => m.role !== "system");
        setMsgs(list.slice(-100));
        setFeedbackMap(fb.data || {});
        setNextReview(nr.data && nr.data.scheduled_at ? nr.data : null);
      } catch { /* keep local */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the check-in card submits, poll history briefly so the new
  // daily_checkin user bubble + assistant reply pop into the timeline.
  useEffect(() => {
    if (!refreshTrigger) return;
    setCollapsed(false);
    setAwaitingCheckin(true);
    let cancelled = false;
    let attempts = 0;
    const seenIds = new Set(msgs.map((m) => m.id));
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${API}/coach/history`, { headers: HEADERS() });
        const list = (r.data || []).filter((m) => m.role !== "system");
        const hasNewAssistant = list.some((m) => m.role === "assistant" && !seenIds.has(m.id));
        if (hasNewAssistant) {
          setMsgs(list.slice(-100));
          setAwaitingCheckin(false);
          return;
        }
      } catch { /* silent */ }
      if (attempts < 15) setTimeout(poll, 2000);
      else setAwaitingCheckin(false);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const send = async (e, retryEventId = null, overrideText = null) => {
    if (e && e.preventDefault) e.preventDefault();
    if (busy) return;
    const text = overrideText != null
      ? overrideText
      : (retryEventId
          ? (msgs.find((m) => m.event_id === retryEventId && m.role === "user")?.content)
          : input.trim());
    if (!text) return;
    const eventId = retryEventId || `chat:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
    if (!retryEventId) {
      setMsgs((s) => [...s, {
        role: "user", content: text, event_id: eventId,
        source: "patient_chat", created_at: new Date().toISOString(),
        id: "tmp-" + Date.now(),
      }]);
      setInput("");
    } else {
      // Clear the error bubble for this event before retrying.
      setMsgs((s) => s.filter((m) => !(m.role === "system_error" && m.event_id === retryEventId)));
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/coach/chat`,
        { message: text, event_id: eventId }, { headers: HEADERS() });
      if (res.data?.ok === false && res.data?.retryable) {
        setMsgs((s) => [...s, {
          role: "system_error", content: "Unable to respond right now.",
          event_id: eventId, id: "err-" + Date.now(),
        }]);
      } else {
        setMsgs((s) => [...s, {
          role: "assistant", content: res.data.reply, event_id: eventId,
          source: "patient_chat", created_at: new Date().toISOString(),
          sources: res.data.sources || [],
          grounding: res.data.grounding || null,
          // iter82 · use the DB id when the backend returns one so the
          // thumbs / feedback POST addresses a real row. Falls back to
          // a synthetic id only if the server doesn't include one.
          id: res.data.message_id || ("resp-" + Date.now()),
        }]);
      }
    } catch (err) {
      setMsgs((s) => [...s, {
        role: "system_error", content: "Unable to respond right now.",
        event_id: eventId, id: "err-" + Date.now(),
      }]);
    } finally { setBusy(false); }
  };

  const first = (userName || "").split(" ")[0] || "there";

  const sendStarter = (text) => {
    // Bypass the stale-closure on `input` by passing the text directly
    // into `send` as an override. Clearing the input is cosmetic since
    // it was never populated in the first place.
    setInput("");
    send(null, null, text);
  };

  const voteFeedback = async (m, helpful) => {
    if (!m.id) return;
    const already = feedbackMap[m.id];
    const nextHelpful = already?.helpful === helpful ? !helpful : helpful;
    setFeedbackMap((s) => ({ ...s, [m.id]: { helpful: nextHelpful, note: already?.note || "" } }));
    setFeedbackOpen((s) => ({ ...s, [m.id]: !nextHelpful }));
    try {
      await axios.post(
        `${API}/coach/messages/${m.id}/feedback`,
        { helpful: nextHelpful, note: (feedbackDraft[m.id] || "").trim() || null },
        { headers: HEADERS() },
      );
    } catch {
      toast.error("Couldn't save that — try again in a moment.");
    }
  };

  const saveFeedbackNote = async (m) => {
    const note = (feedbackDraft[m.id] || "").trim();
    try {
      await axios.post(
        `${API}/coach/messages/${m.id}/feedback`,
        { helpful: false, note: note || null },
        { headers: HEADERS() },
      );
      setFeedbackMap((s) => ({ ...s, [m.id]: { helpful: false, note } }));
      setFeedbackOpen((s) => ({ ...s, [m.id]: false }));
      toast.success("Thanks — your clinician will see this.");
    } catch {
      toast.error("Couldn't save the note.");
    }
  };

  // Batch 11C · patient explicitly asks the coach to flag a no-evidence
  // question for their clinician. Only this action notifies the clinician.
  const flagForClinician = async (m) => {
    if (!m.id) return;
    setFlagState((s) => ({ ...s, [m.id]: "sending" }));
    try {
      await axios.post(`${API}/coach/flag-clinician`,
        { message_id: m.id, question: "" }, { headers: HEADERS() });
      setFlagState((s) => ({ ...s, [m.id]: "done" }));
      toast.success("Flagged for your clinician.");
    } catch {
      setFlagState((s) => { const n = { ...s }; delete n[m.id]; return n; });
      toast.error("Couldn't flag that — try again in a moment.");
    }
  };

  return (
    <div id="coach-chat-panel" className={`fl-card p-0 overflow-hidden flex flex-col ${pulse ? "coach-card-pulse" : ""}`}
         style={collapsed
           ? undefined
           : { height: "clamp(560px, 70vh, 720px)" }}
         data-testid="coach-chat"
         onClick={dismissNewBadge}
         onFocus={dismissNewBadge}>
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        data-testid="coach-chat-toggle"
        aria-label={collapsed ? "Expand coach chat" : "Collapse coach chat"}
        className="p-4 border-b flex items-center gap-2 w-full text-left transition-colors hover:bg-white/[.02]"
        style={{ borderColor: "var(--fl-border)" }}>
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
             style={{ background: "color-mix(in oklab, var(--fl-orange) 20%, var(--fl-bg-2))", color: "var(--fl-orange)" }}>
          <Sparkles size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="fl-heading text-base">AI Recovery Coach</div>
          <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>Powered by Claude · Available 24/7 · Not a replacement for clinician care</div>
        </div>
        <span className="ml-2 opacity-60" style={{ color: "var(--fl-muted)" }}>
          {collapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </button>
      {!collapsed && (
        <>
          {/* Persistent scope note + next clinician review, both compact */}
          <div className="px-4 py-2 border-b flex items-center justify-between gap-3 flex-wrap text-[11px]"
               style={{ borderColor: "var(--fl-border)", background: "var(--fl-bg-2)" }}>
            <div className="flex items-center gap-1.5" style={{ color: "var(--fl-muted)" }}
                 data-testid="coach-scope-note">
              <Shield size={11} className="text-[color:var(--fl-orange)]" />
              <span>AI Coach supports your Functional LAB programme and does not replace medical or emergency care.</span>
            </div>
            {nextReview?.scheduled_at && (
              <div className="flex items-center gap-1.5 shrink-0" style={{ color: "var(--fl-text-2)" }}
                   data-testid="coach-next-review">
                <CalendarClock size={11} className="text-[color:var(--fl-orange)]" />
                <span>
                  Next review:{" "}
                  <b>
                    {new Date(nextReview.scheduled_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  </b>
                  {nextReview.clinician_name ? ` · ${nextReview.clinician_name}` : ""}
                </span>
              </div>
            )}
          </div>
          <div ref={scrollBoxCoach} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="coach-messages">
            {loaded && msgs.length === 0 && (
              <>
                <div className="text-sm p-3 rounded-md" style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}>
                  Hi {first} — I'm your Recovery Coach. Ask me anything about your programme, how you're feeling, or how to progress. If you notice anything concerning, tell me and I'll flag it for your clinician.
                </div>
                <div className="flex flex-wrap gap-2 pt-1" data-testid="coach-starter-chips">
                  {STARTER_CHIPS.map((c, i) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => sendStarter(c)}
                      disabled={busy}
                      className="text-xs px-3 py-1.5 rounded-full transition-colors hover:brightness-110 disabled:opacity-50"
                      style={{
                        background: "color-mix(in oklab, var(--fl-orange) 12%, var(--fl-bg-2))",
                        color: "var(--fl-text)",
                        border: "1px solid color-mix(in oklab, var(--fl-orange) 35%, transparent)",
                      }}
                      data-testid={`coach-starter-${i}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </>
            )}
            {msgs.map((m) => {
              if (m.role === "system_error") {
                return (
                  <div key={m.id} className="flex justify-start" data-testid={`msg-error-${m.event_id}`}>
                    <div className="max-w-[80%] px-3 py-2 rounded-lg text-sm"
                         style={{ background: "color-mix(in oklab, #EF4444 12%, transparent)", color: "#FCA5A5", border: "1px solid color-mix(in oklab, #EF4444 30%, transparent)" }}>
                      <div className="flex items-center gap-2">
                        <AlertTriangle size={13} />
                        <span>{m.content}</span>
                      </div>
                      <button type="button"
                              onClick={() => send(null, m.event_id)}
                              disabled={busy}
                              className="mt-2 text-xs underline hover:no-underline"
                              data-testid={`msg-retry-${m.event_id}`}>
                        Retry
                      </button>
                    </div>
                  </div>
                );
              }
              const isDaily = m.source === "daily_checkin";
              const isWorkout = m.source === "workout_feedback";
              const isClinician = m.role === "assistant" && (m.source === "clinician_reply" || m.clinician_approved);
              const sourceLabel = isClinician
                ? "Clinician"
                : m.role === "assistant"
                  ? "AI Coach"
                  : SOURCE_LABELS[m.source] || "You";
              const fb = feedbackMap[m.id];
              return (
                <div key={m.id} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className="max-w-[80%]">
                    <div className={`px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${(isDaily || isWorkout) && m.role === "user" ? "border" : ""}`}
                         style={{
                           background: m.role === "user"
                             ? ((isDaily || isWorkout) ? "color-mix(in oklab, var(--fl-orange) 14%, var(--fl-bg-2))" : "var(--fl-orange)")
                             : "var(--fl-bg-2)",
                           color: m.role === "user"
                             ? ((isDaily || isWorkout) ? "var(--fl-text)" : "#fff")
                             : "var(--fl-text)",
                           borderColor: (isDaily || isWorkout) ? "color-mix(in oklab, var(--fl-orange) 30%, transparent)" : undefined,
                         }}
                         data-testid={`msg-${m.role}${isDaily ? "-checkin" : isWorkout ? "-workout" : isClinician ? "-clinician" : ""}`}>
                      {m.content}
                    </div>
                    {/* Source label + timestamp + reviewed badge + feedback thumbs */}
                    <div className={`flex items-center gap-2 mt-1 text-[10px] uppercase tracking-widest ${m.role === "user" ? "justify-end" : "justify-start"}`}
                         style={{ color: "var(--fl-muted-2)" }}>
                      <span data-testid={`msg-source-${m.id}`}>{sourceLabel}</span>
                      {m.created_at && <span>· {messageTime(m.created_at)}</span>}
                      {isClinician && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full normal-case tracking-normal"
                              style={{ background: "color-mix(in oklab, #22C55E 14%, transparent)", color: "#86EFAC", border: "1px solid color-mix(in oklab, #22C55E 30%, transparent)" }}
                              data-testid={`msg-reviewed-${m.id}`}>
                          <CheckCircle2 size={9} /> Reviewed by clinician
                        </span>
                      )}
                      {m.id && m.id === newResponseId && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full normal-case tracking-normal font-semibold"
                              style={{ background: "color-mix(in oklab, var(--fl-orange) 20%, transparent)",
                                       color: "var(--fl-orange)",
                                       border: "1px solid color-mix(in oklab, var(--fl-orange) 40%, transparent)" }}
                              data-testid={`msg-new-badge-${m.id}`}>
                          New response
                        </span>
                      )}
                    </div>
                    {/* Thumb buttons on AI messages only — suppress on
                        optimistic bubbles that don't have a real DB id
                        yet (would 404 the feedback POST otherwise). */}
                    {m.role === "assistant" && !isClinician && m.id && !String(m.id).startsWith("resp-") && !String(m.id).startsWith("tmp-") && (
                      <div className={`flex items-center gap-1 mt-1 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <button
                          type="button"
                          onClick={() => voteFeedback(m, true)}
                          className={`p-1 rounded transition-colors ${fb?.helpful === true ? "text-[color:var(--fl-orange)]" : "opacity-50 hover:opacity-100"}`}
                          style={{ color: fb?.helpful === true ? undefined : "var(--fl-muted-2)" }}
                          data-testid={`msg-thumb-up-${m.id}`}
                          aria-label="Mark as helpful">
                          <ThumbsUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => voteFeedback(m, false)}
                          className={`p-1 rounded transition-colors ${fb?.helpful === false ? "text-[#EF4444]" : "opacity-50 hover:opacity-100"}`}
                          style={{ color: fb?.helpful === false ? undefined : "var(--fl-muted-2)" }}
                          data-testid={`msg-thumb-down-${m.id}`}
                          aria-label="Mark as not helpful">
                          <ThumbsDown size={12} />
                        </button>
                      </div>
                    )}
                    {/* Optional "What went wrong?" textarea after thumbs-down */}
                    {feedbackOpen[m.id] && (
                      <div className="mt-2 p-2 rounded-md" style={{ background: "var(--fl-bg-2)", border: "1px solid var(--fl-border)" }}>
                        <textarea
                          value={feedbackDraft[m.id] || ""}
                          onChange={(e) => setFeedbackDraft((s) => ({ ...s, [m.id]: e.target.value }))}
                          placeholder="What went wrong? (optional — helps your clinician improve the coach)"
                          className="fl-input w-full text-xs px-2 py-1.5 rounded resize-y"
                          rows={2}
                          maxLength={500}
                          data-testid={`msg-feedback-note-${m.id}`} />
                        <div className="flex justify-end gap-2 mt-1">
                          <button type="button" onClick={() => setFeedbackOpen((s) => ({ ...s, [m.id]: false }))}
                                  className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                            Cancel
                          </button>
                          <button type="button" onClick={() => saveFeedbackNote(m)}
                                  className="fl-btn-ghost text-[11px]"
                                  data-testid={`msg-feedback-save-${m.id}`}>
                            Save
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  {m.role === "assistant" && (
                    <CoachSources sources={m.sources} id={m.id} />
                  )}
                  {m.role === "assistant" && m.grounding?.flag_offer && (
                    (m.grounding?.flagged || flagState[m.id] === "done") ? (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full"
                           style={{ background: "color-mix(in oklab, #22C55E 14%, transparent)", color: "#86EFAC", border: "1px solid color-mix(in oklab, #22C55E 30%, transparent)" }}
                           data-testid={`coach-flagged-${m.id}`}>
                        <CheckCircle2 size={11} /> Flagged for your clinician
                      </div>
                    ) : (m.id && !String(m.id).startsWith("resp-") && !String(m.id).startsWith("tmp-")) ? (
                      <button type="button" onClick={() => flagForClinician(m)}
                              disabled={flagState[m.id] === "sending"}
                              className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full transition hover:brightness-110 disabled:opacity-50"
                              style={{ background: "color-mix(in oklab, var(--fl-orange) 12%, var(--fl-bg-2))", color: "var(--fl-orange)", border: "1px solid color-mix(in oklab, var(--fl-orange) 35%, transparent)" }}
                              data-testid={`coach-flag-${m.id}`}>
                        <Flag size={11} /> {flagState[m.id] === "sending" ? "Flagging…" : "Flag for my clinician"}
                      </button>
                    ) : null
                  )}
                </div>
              );
            })}
            {busy && (
              <div className="flex justify-start" data-testid="coach-thinking">
                <div className="max-w-[80%] px-3 py-2 rounded-lg text-sm" style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--fl-orange)" }} />
                    Coach is thinking…
                  </span>
                </div>
              </div>
            )}
            {awaitingCheckin && !busy && (
              <div className="flex justify-start" data-testid="coach-awaiting-checkin">
                <div className="max-w-[80%] px-3 py-2 rounded-lg text-sm" style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "var(--fl-orange)" }} />
                    Coach is replying to your check-in…
                  </span>
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={send} className="p-3 border-t flex gap-2 shrink-0 sticky bottom-0" style={{ borderColor: "var(--fl-border)", background: "var(--fl-bg)" }}>
            <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Message your Recovery Coach…"
                   className="fl-input flex-1 text-sm px-3 py-2 rounded-md" disabled={busy} data-testid="coach-input" />
            <button type="submit" disabled={busy || !input.trim()} className="fl-btn text-sm" data-testid="coach-send">
              <Send size={14} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}

/* -------------------- Page -------------------- */
export default function PatientCoach() {
  const { user } = useAuth();
  const [score, setScore] = useState({ score: null, risk: null, weekly_trend: [] });
  const [access, setAccess] = useState({ loading: true, allowed: false });
  const [reload, setReload] = useState(0);

  useEffect(() => {
    axios.get(`${API}/coach/access`, { headers: HEADERS() })
      .then((r) => setAccess({ loading: false, allowed: !!r.data?.allowed }))
      .catch(() => setAccess({ loading: false, allowed: false }));
  }, []);

  useEffect(() => {
    axios.get(`${API}/coach/score/mine`, { headers: HEADERS() })
      .then((r) => setScore(r.data)).catch(() => {});
  }, [reload]);

  if (access.loading) {
    return (
      <DashLayout>
        <div className="max-w-6xl mx-auto text-sm" style={{ color: "var(--fl-muted-2)" }}>Loading…</div>
      </DashLayout>
    );
  }

  if (!access.allowed) {
    return (
      <DashLayout>
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="fl-label text-[color:var(--fl-orange)] mb-1">AI Recovery Coach</div>
            <h1 className="fl-heading text-3xl md:text-4xl">Your daily recovery partner.</h1>
          </div>
          <div className="fl-card p-8" data-testid="coach-locked-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                   style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, var(--fl-bg-2))", color: "var(--fl-orange)" }}>
                <Lock size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="fl-heading text-xl md:text-2xl">Unlock the AI Recovery Coach with a Functional LAB Membership.</h2>
                <p className="text-sm mt-3" style={{ color: "var(--fl-muted)" }}>
                  The AI Recovery Coach is a members-only benefit that gives you 24/7 guidance between appointments,
                  proactive check-ins and a personalised recovery score reviewed by your clinician.
                  Your <strong>daily check-in</strong> stays available for everyone — see below.
                </p>
                <ul className="mt-4 text-sm space-y-2" style={{ color: "var(--fl-muted)" }}>
                  <li className="flex gap-2"><Sparkles size={14} className="mt-0.5 text-[color:var(--fl-orange)]" /> 24/7 chat with our clinician-tuned AI coach</li>
                  <li className="flex gap-2"><TrendingUp size={14} className="mt-0.5 text-[color:var(--fl-orange)]" /> Live Recovery Score with 14-day trend</li>
                  <li className="flex gap-2"><ShieldCheck size={14} className="mt-0.5 text-[color:var(--fl-orange)]" /> Automatic red-flag alerts sent to your clinician</li>
                </ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/app/packages" className="fl-btn text-sm" data-testid="coach-locked-cta">
                    <ArrowRight size={14} /> See memberships
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="fl-label mb-2">Standard access</div>
            <CheckinCard onSubmitted={() => setReload((n) => n + 1)} />
          </div>
        </div>
      </DashLayout>
    );
  }

  return (
    <DashLayout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="fl-label text-[color:var(--fl-orange)] mb-1">AI Recovery Coach</div>
          <h1 className="fl-heading text-3xl md:text-4xl">Your daily recovery partner.</h1>
          <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
            Log a quick check-in, see your Recovery Score, and chat with the coach whenever you need guidance between appointments.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <ScoreGauge score={score.score} risk={score.risk} trend={score.weekly_trend} />
            <CheckinCard onSubmitted={() => setReload((n) => n + 1)} />
          </div>
          <CoachChat userName={user?.name} refreshTrigger={reload} />
        </div>
      </div>
    </DashLayout>
  );
}
